import React, { useCallback, useEffect, useState } from "react";
import styles from "./Palette.module.scss";
import { findColorByHex } from "../../helpers/findColorByHex";
import { colorsGetProducts } from "../../helpers/colorsGetProducts";
import SkeletonSelectedColors from "../SkeletonLoader/SkeletonSelectedColors";
import SkeletonColorMap from "../SkeletonLoader/SkeletonColorMap";
import { SelectionComplete } from "../SelectionComplete/SelectionComplete";
import { getBackgroundColor } from "../../helpers/getBackgroundColor";
import deco from "../../assets/deco.jpg";
import { hex2rgbaStyle } from "../../helpers/hex2rgba";
import { Mosaic } from "../Mosaic/Mosaic";
import { Group, Layer, Stage } from "react-konva";
import { MosaicMask } from "../../helpers/mosaicProcessor";
import useWindowSize from "../../hooks/useWindowSize";

const mosaicDefaultSize = {
  width: 150.75,
  height: 120,
};

function Palette({
  area,
  room,
  layer,
  device,
  search,
  picker,
  spectre,
  setArea,
  selected,
  exterior,
  setPicker,
  terminate,
  setSelected,
  paletteType,
  setTerminate,
  mosaicCombine,
  selectedAreas,
  executionType,
  reformSelected,
  mosaicSelected,
  setSelectedAreas,
  setMosaicCombine,
  setMosaicSelected,
  setReformSelected,
}) {
  const [colors, setColors] = useState([]);
  const [filteredColors, setFilteredColors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeColor, setActiveColor] = useState();
  const [renderResolution, setRenderResolution] = useState(mosaicDefaultSize);
  const size = useWindowSize()?.width;

  useEffect(() => {
    if (size >= 1400) {
      setRenderResolution({
        width: 150.75,
        height: 120,
      });
    } else if (size >= 1200 && size < 1400) {
      setRenderResolution({
        width: 128.75,
        height: 120,
      });
    } else if (size >= 992 && size < 1200) {
      setRenderResolution({
        width: 145.83,
        height: 120,
      });
    } else if (size >= 768 && size < 992) {
      setRenderResolution({
        width: 165,
        height: 120,
      });
    } else if (size < 768) {
      setRenderResolution({
        width: (size - 35) / 2,
        height: 120,
      });
    }
  }, [size]);

  const decoType = executionType === "deco";
  const mosaicType = executionType === "mosaic";

  const decoName = () => {
    if (mosaicType) {
      return "Штукатурка (мозаїчна)";
    } else if (decoType) {
      return "Штукатурка (декоративна)";
    } else return "Фарбування";
  };

  const onColorClick = (hex, code, palette) => {
    setPicker(hex);
    setActiveColor({ hex, code, palette });
  };

  const fetchProducts = useCallback(async () => {
    try {
      await setIsLoading(true);
      const colorMap = {};

      selected.forEach((item) => {
        item.color.forEach((color) => {
          const key = `${color.code}_${item.type}`;

          if (!colorMap[key]) {
            colorMap[key] = {
              hex: color.hex,
              code: color.code,
              tag: [],
              palette: color.palette,
              products: [],
              area: [],
              type: item.type,
            };
          }

          const existingTag = colorMap[key].tag.find(
            (t) => t.type === item.tag.type && t.name === item.tag.name,
          );

          if (!existingTag) {
            colorMap[key].tag.push({
              type: item.tag.type,
              name: item.tag.name,
              subName: item.tag.subName !== "" ? [item.tag.subName] : [],
            });
          } else {
            if (
              item.tag.subName !== "" &&
              !existingTag.subName.includes(item.tag.subName)
            ) {
              existingTag.subName.push(item.tag.subName);
            }
          }
        });
      });

      const result = Object.values(colorMap);

      const mergedArray = await colorsGetProducts(result, exterior);
      // console.log(mergedArray);
      const uniqueAreas = new Set([...selectedAreas, ...area]);
      await setSelectedAreas([...uniqueAreas]);
      await setReformSelected(mergedArray);
      await setIsLoading(false);
    } catch (error) {
      console.error("Error fetching or updating products:", error);
    }
  }, [selected]);

  useEffect(() => {
    fetchProducts().then(() => setIsLoading(false));
  }, [terminate]);

  useEffect(() => {
    const token = "UfRsd63GrrOg8iSyBo";
    setIsLoading(true);

    const params =
      spectre === "all"
        ? {
            query: {
              palette: paletteType.toUpperCase(),
            },
            size: 5000,
            sort: {},
          }
        : {
            query: {
              palette: paletteType.toUpperCase(),
              groups: [spectre],
            },
            size: 500,
            sort: {},
          };

    fetch("https://siltek-pss.kovalska.work/api", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        method: "colors.get.colors",
        params: params,
      }),
    })
      .then(async (response) => {
        const data = await response.json();
        if (data.error) {
          setColors([]);
          console.log(data.error.message);
        } else {
          const result = data.result?.documents.filter(
            ({ palette }) => palette.toLowerCase() === paletteType,
          );

          setColors(result);
          setIsLoading(false);
        }
      })
      .catch((e) => console.log(e.message));
  }, [spectre, paletteType]);

  useEffect(() => {
    const searchLowerCase = search.toLowerCase();

    search?.length > 0
      ? setFilteredColors(
          colors?.filter((color) =>
            color.code.toLowerCase().includes(searchLowerCase),
          ),
        )
      : setFilteredColors(colors);
  }, [search, colors]);

  const onSelected = (activeColor, area) => {
    const newSelect = area.map((i) => {
      const tag = layer.filter((lay) => lay.id === i).map((i) => i.tag)?.[0];
      return { id: i, tag, color: [activeColor], type: executionType };
    });

    if (selected?.length > 0) {
      const newSet = [...selected];

      const filteredSet = newSet.filter((item) => item.type === executionType);

      if (filteredSet.length > 0) {
        newSelect.forEach((item2) => {
          const matchingItem = filteredSet.find(
            (item1) => item1.id === item2.id,
          );

          if (matchingItem) {
            item2.color.forEach((colorToAdd) => {
              const isColorExist = matchingItem.color.some(
                (existingColor) =>
                  existingColor.hex === colorToAdd.hex &&
                  existingColor.code === colorToAdd.code &&
                  existingColor.palette === colorToAdd.palette,
              );

              if (!isColorExist) {
                matchingItem.color.push(colorToAdd);
              }
            });
          } else {
            newSet.push(item2);
          }
        });
      } else {
        newSet.push(...newSelect);
      }

      setSelected(newSet);
    } else {
      setSelected(newSelect);
    }
    setArea([]);
  };

  function removeColor(id, hexToRemove) {
    const updatedSelected = selected.map((item) => {
      if (item.id === id) {
        item.color = item.color?.filter((color) => color.hex !== hexToRemove);
        if (item.color?.length === 0) {
          return null;
        }
      }
      return item;
    });
    const filteredSelected = updatedSelected.filter((item) => item !== null);
    setSelected(filteredSelected);
  }

  const paintSelected = reformSelected.filter(
    (select) => select.type === "paint",
  );
  const decoSelected = reformSelected.filter(
    (select) => select.type === "deco",
  );

  // console.log(reformSelected);

  return (
    <>
      <div
        className={styles.wrapper}
        style={
          device === "mobile"
            ? undefined
            : {
                backgroundColor: `${
                  terminate ? "var(--whiteSmoke-bgr)" : "var(--white)"
                }`,
              }
        }
      >
        <div className={styles.container}>
          {terminate ? (
            <div className={styles.terminate}>
              {isLoading ? (
                <SkeletonSelectedColors />
              ) : (
                <>
                  {paintSelected?.length > 0 && (
                    <>
                      <p className={styles.selectedName}>Фарбування</p>
                      <ul>
                        {paintSelected?.map(
                          ({ hex, code, palette, products, tag }) => (
                            <li key={hex}>
                              <div className={styles.details}>
                                <div
                                  className={styles.sample}
                                  style={{ backgroundColor: hex }}
                                />
                                <div className={styles.description}>
                                  <span>
                                    {tag
                                      .map(
                                        ({ name, subName }) =>
                                          `${name}${subName
                                            .map((i) => ` ${i}`)
                                            .join(", ")}`,
                                      )
                                      .join(", ")}
                                  </span>
                                  <span>{code}</span>
                                  <span>Віяло {palette}</span>
                                </div>
                              </div>
                              {products?.length > 0 && (
                                <>
                                  <h3>Рекомендовані продукти</h3>
                                  <ul className={styles.recomendList}>
                                    {products.map(({ _id, product }) => (
                                      <li key={_id}>{product}</li>
                                    ))}
                                  </ul>
                                </>
                              )}
                            </li>
                          ),
                        )}
                      </ul>
                    </>
                  )}
                  {decoSelected?.length > 0 && (
                    <>
                      <p className={styles.selectedName}>
                        Штукатурка (декоративна)
                      </p>
                      <ul>
                        {decoSelected?.map(
                          ({ hex, code, palette, products, tag }) => (
                            <li key={hex}>
                              <div className={styles.details}>
                                <div className={styles.sample}>
                                  <img src={deco} alt={executionType} />
                                  <div
                                    style={{
                                      backgroundColor: hex2rgbaStyle(hex, 0.7),
                                    }}
                                  ></div>
                                </div>
                                <div className={styles.description}>
                                  <span>
                                    {tag
                                      .map(
                                        ({ name, subName }) =>
                                          `${name}${subName
                                            .map((i) => ` ${i}`)
                                            .join(", ")}`,
                                      )
                                      .join(", ")}
                                  </span>
                                  <span>{code}</span>
                                  <span>Віяло {palette}</span>
                                </div>
                              </div>
                              {products?.length > 0 && (
                                <>
                                  <h3>Рекомендовані продукти</h3>
                                  <ul className={styles.recomendList}>
                                    {products.map(({ _id, product }) => (
                                      <li key={_id}>{product}</li>
                                    ))}
                                  </ul>
                                </>
                              )}
                            </li>
                          ),
                        )}
                      </ul>
                    </>
                  )}
                  {mosaicSelected.length > 0 && (
                    <>
                      <p className={styles.selectedName}>
                        Штукатурка (мозаїчна)
                      </p>
                      <ul>
                        {mosaicSelected?.map(({ id, product, primer, set }) => (
                          <li key={id}>
                            <div className={styles.details}>
                              <div
                                className={styles.sample}
                                // style={{ backgroundColor: hex }}
                              >
                                <Stage
                                  width={renderResolution?.width}
                                  height={renderResolution?.height}
                                >
                                  {set?.map((i) => (
                                    <Layer key={JSON.stringify(i.id)}>
                                      <Group>
                                        <MosaicMask
                                          item={i}
                                          renderResolution={renderResolution}
                                        />
                                      </Group>
                                    </Layer>
                                  ))}
                                </Stage>
                                {/*<img src={back} alt={product} />*/}
                              </div>
                              <div className={styles.description}>
                                <span>{id}</span>
                              </div>
                            </div>
                            {primer && (
                              <div className={styles.mosaicRecomendList}>
                                <h3>Рекомендований колір ґрунт-фарби</h3>
                                <span>{primer}</span>
                                <h3>Рекомендовані продукти</h3>
                                <span>{product}</span>
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </>
              )}
            </div>
          ) : (
            <>
              {isLoading ? (
                <SkeletonColorMap device={device} />
              ) : (
                <>
                  {mosaicType ? (
                    <Mosaic
                      picker={picker}
                      device={device}
                      setPicker={setPicker}
                      mosaicCombine={mosaicCombine}
                      mosaicSelected={mosaicSelected}
                      setMosaicCombine={setMosaicCombine}
                      setMosaicSelected={setMosaicSelected}
                    />
                  ) : (
                    <>
                      <div className={styles.topPalette}>
                        <div className={styles.accent}>
                          <span
                            style={{
                              color: `${getBackgroundColor(activeColor?.hex)}`,
                            }}
                          >
                            {activeColor?.code}
                          </span>
                          {activeColor && decoType && (
                            <img src={deco} alt={executionType} />
                          )}
                          <div
                            style={{
                              backgroundColor: hex2rgbaStyle(
                                activeColor?.hex,
                                0.8,
                              ),
                            }}
                            className={styles.colorCover}
                          ></div>

                          {area?.length > 0 &&
                            activeColor &&
                            device !== "mobile" && (
                              <button
                                className={styles.elect}
                                type="button"
                                onClick={() => onSelected(activeColor, area)}
                              >
                                Додати до обраних
                              </button>
                            )}
                        </div>
                        <ul className={styles.palette}>
                          {filteredColors?.map(
                            ({ _id, code, hex, palette }) => (
                              <li
                                key={_id}
                                style={
                                  findColorByHex(hex, selected)
                                    ? {
                                        border: "1px solid black",
                                        padding: "2px",
                                      }
                                    : null
                                }
                                onClick={() => onColorClick(hex, code, palette)}
                              >
                                <div>
                                  {decoType && (
                                    <img src={deco} alt={executionType} />
                                  )}
                                  <span
                                    style={{
                                      backgroundColor: hex2rgbaStyle(hex, 0.8),
                                    }}
                                  />
                                </div>
                              </li>
                            ),
                          )}
                        </ul>

                        {device === "mobile" && (
                          <>
                            {area?.length > 0 && activeColor && (
                              <button
                                className={styles.elect}
                                type="button"
                                onClick={() => onSelected(activeColor, area)}
                              >
                                Додати до обраних
                              </button>
                            )}
                            {/*<SelectionComplete*/}
                            {/*  room={room}*/}
                            {/*  device={device}*/}
                            {/*  selected={selected}*/}
                            {/*  terminate={terminate}*/}
                            {/*  setTerminate={setTerminate}*/}
                            {/*  mosaicSelected={mosaicSelected}*/}
                            {/*  reformSelected={reformSelected}*/}
                            {/*/>*/}
                          </>
                        )}
                      </div>
                      {selected?.length > 0 && (
                        <div className={styles.selected}>
                          {device !== "mobile" && <h3>{decoName()}</h3>}
                          <div className={styles.colorMap}>
                            {selected
                              ?.filter((i) => i.type === executionType)
                              .map(({ id, color, tag: { name, subName } }) => (
                                <div key={id} className={styles.bottomPalette}>
                                  <span>
                                    Обрані "{name}
                                    {subName !== "" && ` ${subName}`}"
                                  </span>
                                  <ul className={styles.selectedPalette}>
                                    {color?.map(({ hex }, index) => (
                                      <li
                                        key={index}
                                        onClick={() => removeColor(id, hex)}
                                      >
                                        {decoType && (
                                          <img src={deco} alt={executionType} />
                                        )}
                                        <div
                                          style={{
                                            backgroundColor: hex2rgbaStyle(
                                              hex,
                                              0.8,
                                            ),
                                          }}
                                        >
                                          <h5>
                                            <span />
                                          </h5>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
        {device === "mobile" &&
          (mosaicSelected.length > 0 || selected?.length > 0) && (
            <SelectionComplete
              room={room}
              device={device}
              selected={selected}
              terminate={terminate}
              setTerminate={setTerminate}
              mosaicSelected={mosaicSelected}
              reformSelected={reformSelected}
            />
          )}
      </div>
    </>
  );
}

export default Palette;
